import React, { useCallback, useState, useEffect, ChangeEvent, useMemo } from 'react';
import { Paper, Button, Icon, FormControl, InputLabel, makeStyles, Select, MenuItem, FormHelperText, Menu, TextField, Theme }
    from '@material-ui/core';
import { TFunction } from 'i18next';
import clsx from 'clsx';
import produce, { Draft } from 'immer';
import { MonitorTargetType, RobotDefinition, RobotInfo } from '@moderepo/mode-apis';
import { SelectInputOption, capitalizeAll, useDropdown, capitalizeFirst, ITEMS_PER_PAGE_5 } from '@moderepo/mode-app-components';
import { useEditMonitorStyle } from './EditMonitorStyle';
import * as RCUtils from '../../../containers/robot-cloud/RCUtils';
import { MonitorSetupInputErrors, EditMonitorStepProps } from './EditMonitorInfoStep';
import { RCRobotsListingContainer } from '../../../containers/robot-cloud';
import { RobotsTableHiddenFields } from '../../../containers/robot-cloud/RCRobotsListingContainer';


const useStyle = makeStyles((theme: Theme) => {
    return {
        targetSetupForm: {
            display      : 'flex',
            flexDirection: 'column',

            '&.readonly': {
                pointerEvents: 'none',
            },

            '& .form-control': {
                width: '100%',
            },

            [theme.breakpoints.up('sm')]: {
                flexDirection : 'row',
                justifyContent: 'space-between',

                '& .form-control': {
                    width          : 'auto',
                    '&:first-child': {
                        marginRight: theme.spacing(1),
                    },
                    '&:last-child:not(:first-child)': {
                        marginLeft: theme.spacing(1),
                        flex      : 1,
                    },
                    '&:not(:last-child)': {
                        marginBottom: 0,
                    },
                },
            },
        },

        targetTypeInput: {
            minWidth: '10em',
        },


        robotsMenuTrigger: {
            cursor   : 'pointer',
            '& input': {
                cursor: 'pointer',
            },
        },

        robotsMenu: {
            width                       : '100%',
            [theme.breakpoints.up('md')]: {
                width: 600,
            },
            [theme.breakpoints.up('lg')]: {
                width: 800,
            },
            padding: 0,
            '& ul' : {
                width  : '100%',
                padding: 0,
            },
        },

        robotsListWrapper: {
            width  : '100%',
            padding: 0,
        },

        robotsList: {
            width  : '100%',
            padding: 0,
        },

    };
});



/**
 * Create a Array of Monitor Target Type options. This will be used to show the user a list of target types he/she can choose from.
 * @param trans
 */
const useCreateTargetTypeOptions = (trans: TFunction): readonly SelectInputOption<MonitorTargetType>[] => {
    const [options, setOptions] = useState<readonly SelectInputOption<MonitorTargetType>[]>([]);

    useEffect(() => {
        setOptions(Object.values(MonitorTargetType).map((targetType: MonitorTargetType): SelectInputOption<MonitorTargetType> => {
            return {
                label: capitalizeAll(trans(RCUtils.getMonitorTargetTypeName(targetType))),
                value: targetType,
            };
        }));
    }, [trans]);

    return options;
};



/**
 * Custom hook to create an Array of input Options from the list of robot classes. This options will be used in the robot Class dropdown
 * @param robotCatalog
 */
const useCreateRobotClassesOptions = (
    robotCatalog: readonly RobotDefinition[] | undefined, trans: TFunction,
): readonly SelectInputOption<string>[] => {
    const [options, setOptions] = useState<readonly SelectInputOption<string>[]>([]);

    useEffect(() => {
        if (robotCatalog) {
            setOptions(robotCatalog.map((robotDef: RobotDefinition): SelectInputOption<string> => {
                return {
                    label: `${robotDef.name} (${robotDef.robotClass})`,
                    value: robotDef.robotClass,
                };
            }));
        } else {
            setOptions([]);
        }
    }, [robotCatalog, trans]);

    return options;
};



/**
 * Errors fields for Monitor Target setup step
 */
interface MonitorTargetInputErrors extends MonitorSetupInputErrors {
    monitorTargetType?: string;
    targetRobotClass?: string;
    targetRobotId?: string;
}



interface EditMonitorTargetStepProps extends EditMonitorStepProps {
    readonly preSelected: boolean;
    readonly robotCatalog: readonly RobotDefinition[] | undefined;
    readonly monitorTargetType: MonitorTargetType | undefined;
    readonly targetRobotClass: string | undefined;
    readonly targetRobotId: string | undefined;
    readonly targetHomeId: number | undefined;
    readonly onStepCompleted: (
        targetType: MonitorTargetType, targetRobotClass: string | undefined, targetRobotId: string | undefined, targetHomeId: number | undefined
    )=> void;
}


/**
 * This is the HTML for the Setup Monitor Target step content
 * @param props
 */
export const EditMonitorTargetStep = (props: EditMonitorTargetStepProps) => {

    const { trans } = props;
    const globalClasses = useEditMonitorStyle();
    const classes = useStyle();


    // These hold the current value of the user input. We will initialize these with the values from props
    const [monitorTargetTypeInput, setMonitorTargetTypeInput] = useState<MonitorTargetType | undefined>(props.monitorTargetType);
    const [targetRobotClassInput, setTargetRobotClassInput] = useState<string | undefined>(props.targetRobotClass);
    const [targetRobotIdInput, setTargetRobotIdInput] = useState<string | undefined>(props.targetRobotId);
    const [targetHomeIdInput, setTargetHomeIdInput] = useState<number | undefined>(props.targetHomeId);
    const targetChanged = useMemo(() => {
        return !props.isNewMonitor && (
            monitorTargetTypeInput !== props.monitorTargetType
            || targetRobotClassInput !== props.targetRobotClass
            || targetRobotIdInput !== props.targetRobotId);
    }, [props.isNewMonitor, props.monitorTargetType, props.targetRobotClass, props.targetRobotId, monitorTargetTypeInput, targetRobotClassInput,
        targetRobotIdInput]);

    const [robotIdMenuAnchorEl, isRobotIdMenuOpened, onRobotIdAnchorElClicked, onRobotIdMenuClosed] = useDropdown();


    // These are the set of options used for the Dropdown/Select input
    const targetTypeOptions = useCreateTargetTypeOptions(trans);
    const robotClassOptions = useCreateRobotClassesOptions(props.robotCatalog, trans);
    const [inputErrors, setInputErrors] = useState<MonitorTargetInputErrors>({ });



    /**
     * Validate Monitor Info form and dispatch step completed if success
     */
    const validateInputs = useCallback((dispatchOnComplete: boolean = true, dispatchOnError: boolean = true): boolean => {

        const newInputErrors: MonitorTargetInputErrors = {};


        // Validate the values and update the error object if there is any error
        if (!monitorTargetTypeInput || !targetRobotClassInput || !targetRobotIdInput) {
            if (!monitorTargetTypeInput) {
                // if targetType is not selected, show "target type is required" error
                newInputErrors.monitorTargetType = 'error.input.monitor_target.type.empty';
            }
            if (monitorTargetTypeInput === MonitorTargetType.ROBOT_CLASS && !targetRobotClassInput) {
                // if targetType is ROBOT_CLASS but robotClass is not selected, show "robot class is required" error
                newInputErrors.targetRobotClass = 'error.input.monitor_target.robot_class.empty';
            }
            if (monitorTargetTypeInput === MonitorTargetType.ROBOT_ID && !targetRobotIdInput) {
                // if targetType is DEVICE_ID but deviceId is not selected, show "device id is required" error
                newInputErrors.targetRobotId = 'error.input.monitor_target.device_id.empty';
            }
        }


        // Update Local input errors
        setInputErrors(newInputErrors);

        // If there is AT LEAST 1 field in the newInputError, this Step is considered to contain errors.
        const hasErrors = Object.keys(newInputErrors).length > 0;

        // If 'dispatchOnError' is specified, dispatch the error or null to the container
        if (dispatchOnError) {
            props.onStepError(hasErrors);
        }


        if (!hasErrors && dispatchOnComplete && monitorTargetTypeInput) {
            // Only dispatch onComplete if dispatchOnComplete is true. Sometime we just want to validate the input but not submit the form.
            props.onStepCompleted(monitorTargetTypeInput, targetRobotClassInput, targetRobotIdInput, targetHomeIdInput);
        }

        return !hasErrors;
    }, [props, monitorTargetTypeInput, targetRobotClassInput, targetRobotIdInput, targetHomeIdInput]);



    const robotsTableHiddenFields = useMemo((): RobotsTableHiddenFields => {
        return {
            preview                : true,
            availability           : true,
            createdAt              : true,
            updatedAt              : true,
            overrideRobotDefinition: true,
        };
    }, []);


    return (
        <div className={globalClasses.stepContentBody}>

            {targetTypeOptions.length > 0 && robotClassOptions.length && (
                <div className={clsx(globalClasses.stepContentForm)}>
                    <div className={clsx(globalClasses.stepSubtitle)}>
                        {trans('create_monitor.step_target.subtitle')}
                        {props.preSelected && (
                            <span className="note">
                                &nbsp;{capitalizeFirst(trans('label.pre_selected'))}
                            </span>
                        )}
                    </div>

                    <Paper elevation={1} className={clsx(globalClasses.formControlGroup, classes.targetSetupForm, props.preSelected && 'readonly')}>
                        <FormControl
                            className={clsx(globalClasses.formControl, classes.targetTypeInput, 'form-control')}
                            required
                            key={props.monitorTargetType}
                            error={Boolean(inputErrors.monitorTargetType)}
                        >
                            <InputLabel shrink>
                                {capitalizeAll(trans('label.monitor_target_by'))}
                            </InputLabel>
                            <Select
                                displayEmpty
                                value={monitorTargetTypeInput || ''}
                                onChange={(event: ChangeEvent<{ value: unknown }>) => {
                                    // Input changed so clear the error and update the state
                                    props.onStepError(false);
                                    setInputErrors(produce(inputErrors, (draft: Draft<MonitorTargetInputErrors>) => {
                                        delete draft.monitorTargetType;
                                    }));

                                    setMonitorTargetTypeInput(event.target.value as MonitorTargetType);

                                    // because the user changed targetType, we need to clear the previous selected target
                                    setTargetRobotClassInput(undefined);
                                    setTargetRobotIdInput(undefined);
                                    setTargetHomeIdInput(undefined);
                                }}
                                onBlur={() => {
                                    // on done editing, validate input but don't submit it until the user click next
                                    // validateInputs(false);
                                }}
                            >
                                <MenuItem key="empty" value="" disabled>
                                    {capitalizeFirst(trans('label.select_one'))}
                                </MenuItem>
                                {targetTypeOptions.map((option: SelectInputOption<MonitorTargetType>) => {
                                    return (
                                        <MenuItem key={option.label} value={option.value}>
                                            {option.label}
                                        </MenuItem>
                                    );
                                })}
                            </Select>
                            {inputErrors.monitorTargetType && (
                                <FormHelperText>
                                    {trans(inputErrors.monitorTargetType)}
                                </FormHelperText>
                            )}
                        </FormControl>

                        {monitorTargetTypeInput === MonitorTargetType.ROBOT_CLASS && (
                            <>
                                {props.preSelected && targetHomeIdInput && (
                                    <TextField
                                        className={clsx(globalClasses.formControl, 'form-control')}
                                        required
                                        variant="standard"
                                        type="text"
                                        label={capitalizeAll(trans('label.home_id'))}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        InputProps={{
                                            readOnly: true,
                                        }}
                                        value={targetHomeIdInput || ''}
                                    />
                                )}
                                <FormControl
                                    className={clsx(globalClasses.formControl, 'form-control')}
                                    required
                                    error={Boolean(inputErrors.targetRobotClass)}
                                >
                                    <InputLabel shrink>
                                        {capitalizeAll(trans('label.robot_class'))}
                                    </InputLabel>
                                    <Select
                                        displayEmpty
                                        value={targetRobotClassInput || ''}
                                        onChange={(event: ChangeEvent<{ value: unknown }>) => {
                                        // Input changed so clear the error and update the state
                                            props.onStepError(false);
                                            setInputErrors(produce(inputErrors, (draft: any) => {
                                                delete draft.targetRobotClass;
                                            }));
                                            setTargetRobotClassInput(event.target.value as string);
                                        }}
                                        onBlur={() => {
                                        // on done editing, validate input but don't submit it until the user click next
                                        // validateInputs(false);
                                        }}
                                    >
                                        <MenuItem key="empty" value="" disabled>
                                            {capitalizeFirst(trans('label.select_one'))}
                                        </MenuItem>
                                        {robotClassOptions.map((option: SelectInputOption<string>) => {
                                            return (
                                                <MenuItem key={option.label} value={option.value}>
                                                    {option.label}
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                    {inputErrors.targetRobotClass && (
                                        <FormHelperText>
                                            {trans(inputErrors.targetRobotClass)}
                                        </FormHelperText>
                                    )}
                                </FormControl>
                            </>
                        )}


                        {monitorTargetTypeInput === MonitorTargetType.ROBOT_ID && (
                            <>
                                <TextField
                                    className={clsx(globalClasses.formControl, 'form-control')}
                                    required
                                    variant="standard"
                                    type="text"
                                    fullWidth
                                    label={capitalizeAll(trans('label.robot_id'))}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    InputProps={{
                                        readOnly    : true,
                                        endAdornment: (
                                            <Icon>arrow_drop_down</Icon>
                                        ),
                                        className: classes.robotsMenuTrigger,
                                    }}
                                    error={Boolean(inputErrors?.targetRobotId)}
                                    helperText={inputErrors.targetRobotId ? trans(inputErrors.targetRobotId) : ''}
                                    value={targetRobotIdInput || ''}
                                    onClick={onRobotIdAnchorElClicked}
                                />

                                <Menu
                                    getContentAnchorEl={null}
                                    anchorEl={robotIdMenuAnchorEl}
                                    anchorOrigin={{
                                        vertical  : 'bottom',
                                        horizontal: 'right',
                                    }}
                                    transformOrigin={{
                                        vertical  : 'top',
                                        horizontal: 'right',
                                    }}
                                    PaperProps={{
                                        className: classes.robotsMenu,
                                    }}
                                    keepMounted
                                    open={isRobotIdMenuOpened}
                                    onClose={onRobotIdMenuClosed}
                                >
                                    <span className={classes.robotsListWrapper}>
                                        <RCRobotsListingContainer
                                            className={classes.robotsList}
                                            projectId={props.projectId}
                                            gatewayDependentRobotsOnly={false}
                                            title={capitalizeFirst(trans('panel.select_robot.title'))}
                                            canCreateRobots={false}
                                            canLinkRobots={false}
                                            itemsPerPage={ITEMS_PER_PAGE_5}
                                            hiddenFields={robotsTableHiddenFields}
                                            onRobotSelected={(robot: RobotInfo) => {
                                                // Input changed so clear the error and update the state
                                                props.onStepError(false);
                                                setInputErrors(produce(inputErrors, (draft: any) => {
                                                    delete draft.targetRobotId;
                                                }));

                                                // when user select a robot, we will need to set BOTH robotId and homeId
                                                setTargetRobotIdInput(robot.id);
                                                setTargetHomeIdInput(robot.homeId);
                                                onRobotIdMenuClosed();
                                            }}
                                        />
                                    </span>
                                </Menu>
                            </>
                        )}
                    </Paper>
                </div>
            )}

            <div className={globalClasses.stepContentActionsContainer}>
                <Button
                    disabled={!props.canGoBack}
                    onClick={() => {
                        // Validate the inputs AND dispatch onComplete if successful so that we can save the user inputs before going back.
                        // However, if inputs are invalid, we will not dispatch error and just take the user back to the previous step. We will not
                        // save the user's input since it is invalid anyway.
                        validateInputs(true, false);
                        props.onPrevStepClick();
                    }}
                    className={globalClasses.navButton}
                    startIcon={<Icon>navigate_before</Icon>}
                >
                    {capitalizeAll(trans('label.back'))}
                </Button>
                <Button
                    onClick={() => {
                        if (validateInputs(true)) {
                            props.onNextStepClick();
                        }
                    }}
                    className={globalClasses.navButton}
                    endIcon={<Icon>navigate_next</Icon>}
                >
                    {capitalizeAll(trans(props.isLastStep ? 'label.finish' : 'label.next'))}
                </Button>

                {/*
                    For Monitor Target step, the agent are not allowed to finish the setup flow he he changed the target because if the target
                    is modified, the condition will need to be updated as well. So, we will only enable this button if the agent did not
                    change anything in this step
                */}
                {!props.isNewMonitor && !targetChanged && (
                    <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => {
                            if (props.onGotoLastStepClick && validateInputs(true)) {
                                props.onGotoLastStepClick();
                            }
                        }}
                        className={clsx(globalClasses.navButton, 'finish')}
                    >
                        {capitalizeAll(trans('label.finish'))}
                    </Button>
                )}
            </div>
        </div>
    );
};
